import { Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { connect } from 'react-redux'
import * as Redux from 'redux'
import ClientInsuranceActions from '@ClientActions/insurance.action'
import {
    ICommonQuery,
    IPageHeaderActions,
    MCommonProduct,
} from '@Models/common.model'
import ClientFirearmActions from '@ClientActions/firearm.action'
import moment from 'moment'
import { APIServices } from '@Services/api.service'
import { CActions } from '@Constants/common.constant'
import { RedditTextField } from '@Components/Form/RedditTextField/RedditTextField'
import CustomModal from '@Components/Modal/CustomModal/CustomModal'
import PageHeader from '@Components/Pages/PageHeader/PageHeader'
import './insurance.scss'
import AuthAction from '@Stores/auth/auth.action'
import InsuranceForm from './InsuranceForm'
import Firearms from './Firearms'

const InsuranceModal = () => {
    return (
        <>
            <div className="modal-body p-0">
                <Grid container spacing={3} className="mb-4">
                    <Grid item sm={6} xs={12}>
                        <RedditTextField
                            label="Carrier"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="Hoeger-Nolan"
                        />
                        <RedditTextField
                            label="Address"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="9 Old Shore Drive"
                        />
                        <RedditTextField
                            label="State"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="Delaware"
                        />
                        <RedditTextField
                            label="Confirmation code"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="67457-255"
                        />
                        <RedditTextField
                            label="Expire date"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="10/26/2021"
                        />
                        <RedditTextField
                            label="Coverage"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="Full"
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <RedditTextField
                            label="Tel"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="302-381-6757"
                        />
                        <RedditTextField
                            label="City"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="Newark"
                        />
                        <RedditTextField
                            label="Zip"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="19714"
                        />
                        <RedditTextField
                            label="Start date"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="10/26/2020"
                        />
                        <RedditTextField
                            label="Policy"
                            className="reddit-input mb-2 w-100"
                            variant="filled"
                            value="91330"
                        />
                    </Grid>
                </Grid>
            </div>
            <div className="modal-footer">
                <span className="modal-action">Confirm and Save</span>
                <div className="divider" />
            </div>
        </>
    )
}

const defaultValues = {
    carrier: '',
    phone: '',
    address: '',
    state: '',
    city: '',
    zip: '',
    confirmationCode: '',
    startedAt: null,
    endedAt: null,
    coverage: '',
    policy: '',
}

// models section
interface IInsurance {
    getInsurances: (params?: ICommonQuery) => void
    addInsurance: (body: any) => Promise<any>
    getFirearms: (params?: ICommonQuery) => void
    updateInsurance: (id: string, payload: any) => Promise<any>
    firearms: MCommonProduct[]
    insurances: any[]
    getReground: (type: string, parentId: string, stepTwo?: string) => void
}

function Insurance(props: IInsurance) {
    // core section
    const {
        getInsurances,
        addInsurance,
        getFirearms,
        updateInsurance,
        firearms,
        insurances,
        getReground,
    } = props
    const [showModal, setShowModal] = useState(false)
    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        reset,
    } = useForm({ defaultValues })

    // states section
    const [disabled, setDisabled] = useState(true)
    const [headerActions, setHeaderActions] = useState<IPageHeaderActions[]>([
        CActions.edit,
    ])

    // hooks section
    /**
     * get data from API when first load
     */
    useEffect(() => {
        getInsurances({ limit: 1 })
        getFirearms()
        return () => {
            APIServices.axiosCancelToken()
        }
    }, [])

    /**
     * perform reset form when insurances changed
     */
    useEffect(() => {
        const city = insurances?.[0]?.city
        const state = insurances?.[0]?.state
        APIServices.axiosConfig()

        // if (getReground && state) getReground('STATE', 'US', 'STATE')
        getReground('STATE', 'US', 'STATE')
        if (getReground && city) getReground('CITY', state, 'CITY')

        reset(insurances?.[0] || defaultValues)

        return () => {
            APIServices.axiosCancelToken()
        }
    }, [insurances])

    // functions section
    /**
     * handle submit and create new insurance
     * @param formData inputs value
     */
    const onSubmit = (formData: any) => {
        setDisabled(true)
        const itemIds = firearms?.map((firearm: MCommonProduct) => firearm?.id)
        const startedAt = moment(formData?.startedAt).toISOString()
        const endedAt = moment(formData?.endedAt).toISOString()
        const body = {
            ...formData,
            startedAt,
            endedAt,
            itemIds,
        }
        if (!formData?.coverage) delete body?.coverage
        if (!formData?.items) delete body?.items
        if (insurances?.length) {
            const id = insurances?.[0]?.id
            updateInsurance(id, body)
                .then(() => {
                    setHeaderActions([CActions.edit])
                })
                .catch(() => {
                    setDisabled(false)
                })
        } else {
            addInsurance(body)
                .then(() => {
                    setHeaderActions([CActions.edit])
                    setDisabled(false)
                })
                .catch(() => {
                    setDisabled(false)
                })
        }
    }

    /**
     * handle header action
     * @param action value from header component
     */
    const handleHeaderActions = (action?: string) => {
        switch (action) {
            case 'save':
                handleSubmit(onSubmit)()
                break
            case 'edit':
                setHeaderActions([CActions.save])
                setDisabled(false)
                break

            default:
                break
        }
    }

    // main section
    return (
        <div className="insurance">
            <PageHeader
                title="Insurance"
                actions={headerActions}
                getActions={handleHeaderActions}
            />
            <div className="page-min-height">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <InsuranceForm
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        watch={watch()}
                        disabled={disabled}
                    />
                </form>
                <p className="page-title m-0 mb-3">Firearms Covered</p>
                <Grid container spacing={2}>
                    <Firearms data={firearms} />
                </Grid>
            </div>
            <CustomModal
                modalTitle="Review and Save"
                open={showModal}
                onClose={() => setShowModal(false)}
            >
                <InsuranceModal />
            </CustomModal>
        </div>
    )
}

// store section
const mapStateToProps = (store: any) => {
    const { register, user } = store?.auth
    const { firearms } = store?.client
    const { insurances } = store?.client?.insurances

    return {
        register,
        user,
        firearms: firearms?.firearmData,
        insurances,
    }
}

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
    const { getInsurances, addInsurance, updateInsurance } =
        ClientInsuranceActions
    const { getItems } = ClientFirearmActions
    const { getReground } = AuthAction

    return {
        getInsurances: (params?: ICommonQuery) =>
            dispatch(getInsurances(params) as any),
        addInsurance: (payload: any) => dispatch(addInsurance(payload) as any),
        getFirearms: (params: any) =>
            dispatch(getItems('FIREARM', params) as any),
        updateInsurance: (id: string, payload: any) =>
            dispatch(updateInsurance(id, payload) as any),
        getReground: (type: string, parentId: string, stepTwo?: string) =>
            dispatch(getReground(type, parentId, stepTwo) as any),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Insurance)
